/**
 * Was ist angekommen? Das Zustellbuch einer einzelnen Verbindung.
 *
 * ## Der Fehler, gegen den das gebaut ist
 *
 * Eine Mobilfunkverbindung, die im Wald abreißt, wird von keiner Seite
 * geschlossen — sie schweigt nur. Der Socket bleibt dabei `OPEN`, `send()`
 * wirft nichts, und die App hält sich für verbunden, bis ihr Herzschlag nach
 * `heartbeatTimeoutMs` ohne Antwort aufgibt. Alles, was sie bis dahin
 * hinausschickt, ist weg: nicht angekommen und nicht im Funklochpuffer, denn
 * dorthin geht nur, was gar nicht erst hinausging.
 *
 * Auf der Ausfahrt 555769 am 17.09.2026 waren das bei zwei Funklöchern 41 und
 * 47 Sekunden, jedes Mal am Anfang des Lochs. Das Gerät hat gemessen — das
 * Bildschirmfoto von 16:55 zeigt 27 km/h nach Norden bei grünem Knopf —, und
 * beim Server kam diese Messung nie an (A-137).
 *
 * ## Woran eine Ankunft zu erkennen ist
 *
 * Am `pong`. Der Server arbeitet die Nachrichten einer Verbindung streng
 * nacheinander ab, und das `pong` schickt er erst, wenn er beim `ping`
 * angekommen ist. Kommt es an, hat er alles verarbeitet, was **vor** diesem
 * `ping` hinausging — auf derselben Verbindung, in derselben Reihenfolge.
 * Was **danach** hinausging, ist damit noch nicht bestätigt, auch wenn das
 * `pong` später eintrifft.
 *
 * Das braucht keine neue Nachricht im Protokoll: `pong` trägt `sentAt`
 * unverändert zurück, und daran lässt sich der `ping` wiederfinden.
 *
 * ## Warum kein Zeitfenster
 *
 * Näher lag, einfach die letzten sechzig Sekunden vorzuhalten — so lange, wie
 * der Herzschlag höchstens braucht. Das stimmt aber nur, solange Antworten
 * sofort kommen: Ein `pong`, das auf einer schmalen Leitung zehn Sekunden
 * unterwegs war, schiebt die Erkennung um genau diese zehn Sekunden hinaus,
 * und die ersten Messungen danach fielen aus dem Fenster. Das Buch weiß es
 * genau, statt es zu schätzen.
 *
 * ## Was ein Buch nicht kann
 *
 * Es lebt im Speicher. Wird die App in dem Augenblick beendet, in dem die
 * Leitung schon tot ist und die Verbindung es noch nicht weiß, ist das
 * Unbestätigte verloren. Der Funklochpuffer überdauert das; dieses Buch nicht.
 *
 * Ein Buch gehört zu **einem** Socket. Die Nummerierung der Rahmen beginnt mit
 * jeder neuen Verbindung von vorn, und ein `pong` des einen sagt über den
 * anderen nichts.
 */
export class DeliveryLedger {
    /** Wie viele Rahmen auf dieser Verbindung hinausgegangen sind. */
    #frames = 0;
    /** Gesendete `ping`, deren `pong` aussteht — mit der Zahl der Rahmen davor. */
    #pings = [];
    /** Zustellpflichtiges, das noch kein `pong` bestätigt hat, in Sendereihenfolge. */
    #open = [];
    /** Ein Rahmen ging hinaus, dessen Ankunft niemand nachhalten muss. */
    sent() {
        this.#frames += 1;
    }
    /** Ein Rahmen ging hinaus, der ankommen **muss**. */
    sentTracked(item) {
        this.#open.push({ frame: this.#frames, item });
        this.#frames += 1;
    }
    /** Ein `ping` ging hinaus. */
    pingSent(sentAt) {
        this.#pings.push({ sentAt, framesBefore: this.#frames });
        this.#frames += 1;
    }
    /**
     * Ein `pong` ist da: Alles vor dem zugehörigen `ping` ist angekommen.
     *
     * Ein `pong`, zu dem kein `ping` bekannt ist, bestätigt nichts — etwa eines,
     * das nach dem Abschreiben noch eintrifft.
     */
    pongReceived(sentAt) {
        const index = this.#pings.findIndex((ping) => ping.sentAt === sentAt);
        const ping = this.#pings[index];
        if (ping === undefined) {
            return;
        }
        // Ältere `ping` sind mit bestätigt: Der Server beantwortet sie der Reihe nach.
        this.#pings.splice(0, index + 1);
        this.#open = this.#open.filter((eintrag) => eintrag.frame >= ping.framesBefore);
    }
    /** Wie viel noch auf eine Bestätigung wartet. */
    get unconfirmed() {
        return this.#open.length;
    }
    /**
     * Die Verbindung ist verloren: alles Unbestätigte herausgeben, in der
     * Reihenfolge, in der es hinausging, und das Buch leeren.
     */
    takeUnconfirmed() {
        const offen = this.#open.map((eintrag) => eintrag.item);
        this.#open = [];
        this.#pings = [];
        return offen;
    }
}
//# sourceMappingURL=delivery.js.map